import React, { useState } from "react";
import { Check, Lock, ScanLine } from "lucide-react";
import { SETUP_PHASES, useSetupProgress } from "@/lib/setupProgress";
import { TID } from "@/lib/testIds";
import AccountStep from "@/components/setup/AccountStep";
import ProvidersStep from "@/components/setup/ProvidersStep";
import ClientsStep from "@/components/setup/ClientsStep";
import LinkStep from "@/components/setup/LinkStep";
import LockedStep from "@/components/setup/LockedStep";

const TESTID_FOR_STEP = {
  account: TID.stepBtnAccount,
  profile: TID.stepBtnProfile,
  providers: TID.stepBtnProviders,
  clients: TID.stepBtnClients,
  link: TID.stepBtnLink,
};

// The Practice Setup flow, extracted so it can render both standalone and inside Settings.
export default function PracticeSetupPanel() {
  const { completion, completed, total, percent } = useSetupProgress();
  const [active, setActive] = useState("account");

  const renderStep = () => {
    switch (active) {
      case "account":   return <AccountStep />;
      case "providers": return <ProvidersStep />;
      case "clients":   return <ClientsStep />;
      case "link":      return <LinkStep />;
      default:          return <LockedStep stepId={active} />;
    }
  };

  return (
    <div data-testid={TID.setupRoot} className="flex flex-col gap-4 fade-up w-full">
      <div className="flex items-end gap-4">
        <div>
          <div className="text-[16px] font-bold tracking-[-0.4px] text-[var(--ink)]">Practice Setup</div>
          <div className="text-[12px] text-[var(--gray2)] mt-1 max-w-[560px] leading-relaxed">
            Complete these steps to start submitting claims. Everything autosaves — leave and come back anytime.
          </div>
        </div>
        <div className="ml-auto text-[11px] font-semibold text-[var(--ink)] whitespace-nowrap">
          <span data-testid={TID.setupProgressPct}>{percent}%</span>
          <span className="text-[var(--gray2)] font-normal"> · {completed} of {total} steps</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[302px_1fr] gap-4 items-start">
        {/* Phase list */}
        <div className="flex flex-col gap-2.5">
          {SETUP_PHASES.map((phase, i) => {
            const phaseDone = phase.steps.filter((s) => completion[s.id]).length;
            return (
              <div key={phase.id} className="glass-surface rounded-[20px] overflow-hidden">
                <div className="px-4 py-3 flex items-center gap-2.5 border-b border-black/[.06] bg-white/[.35]">
                  <div
                    className={`w-[21px] h-[21px] rounded-[7px] text-[10px] font-bold flex items-center justify-center ${
                      phaseDone === phase.steps.length
                        ? "bg-[var(--green)] text-white shadow-[0_2px_8px_rgba(52,199,89,.4)]"
                        : "bg-black/[.07] text-[var(--gray1)]"
                    }`}
                  >
                    {i + 1}
                  </div>
                  <div className="text-[11px] font-semibold text-[var(--ink)] flex-1">{phase.name}</div>
                  <div className="text-[9px] text-[var(--gray3)] font-semibold">
                    {phaseDone} / {phase.steps.length}
                  </div>
                </div>
                {phase.steps.map((s) => {
                  const done = !!completion[s.id];
                  const sel = active === s.id;
                  const testid = TESTID_FOR_STEP[s.id];
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={testid}
                      onClick={() => setActive(s.id)}
                      className={`w-full text-left flex items-center gap-2.5 px-4 py-2.5 border-b border-black/[.045] last:border-b-0 transition ${
                        sel ? "bg-[var(--ink)]" : "hover:bg-white/55"
                      }`}
                    >
                      <span
                        className={`w-[18px] h-[18px] rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 ${
                          done
                            ? "bg-[var(--green)] text-white"
                            : sel
                            ? "border-[1.5px] border-white/40 bg-white/20"
                            : "border-[1.5px] border-[var(--gray4)]"
                        }`}
                      >
                        {done ? <Check size={10} /> : null}
                      </span>
                      <span
                        className={`text-[11px] flex-1 min-w-0 ${
                          sel ? "text-white font-semibold" : "text-[var(--ink2)] font-medium"
                        }`}
                      >
                        {s.name}
                      </span>
                      {s.live ? (
                        <span
                          className={`text-[8px] font-bold tracking-[.05em] px-1.5 py-0.5 rounded-[5px] inline-flex items-center gap-0.5 ${
                            sel ? "bg-white/90 text-[var(--ink)]" : "bg-[var(--ink)] text-white"
                          }`}
                        >
                          <ScanLine size={8} />
                          LENS
                        </span>
                      ) : (
                        <Lock size={11} className={sel ? "text-white/60" : "text-[var(--gray3)]"} />
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Detail panel */}
        <div className="glass-surface rounded-[26px] p-6 min-h-[560px] flex flex-col" style={{ boxShadow: "var(--sh-glass-lg)" }}>
          {renderStep()}
        </div>
      </div>
    </div>
  );
}
